import { MODULE_FEATURES } from "@cafe/shared";
import { Badge, Button, Card, CardHeader, Checkbox, DataTable, dialogs, EmptyState, Field, FormGrid, Input, Modal, MoneyInput, NumberInput, PageHeader, Select, Skeleton, toast } from "@cafe/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorText, http, money, useCan } from "../lib/api";
import { featureLabel } from "../lib/labels";

type Price = { currency: string; period: string; amount: number };
type Version = { id: string; version: number; features: string[]; limits: Record<string, number | null>; trialDays: number; prices: Price[]; subscriptions: number };
type Plan = { id: string; code: string; name: string; versions: Version[]; setupFees: { currency: string; amount: number }[] };
type Addon = { id: string; code: string; name: string; description: string | null; grantsFeatures: string[]; limitDeltas: Record<string, number>; pricingModel: string; unitLabel: string | null; requiresAddons: string[]; trialDays: number; isPublic: boolean; prices: Price[]; availability: { planId: string; mode: string }[] };
const PERIODS = ["month", "quarter", "year"] as const;
const PERIOD_LABEL: Record<string, string> = { month: "месяц", quarter: "квартал", year: "год" };

/** Новая версия тарифа (US-PA-05): состав и цены; действующие подписки остаются на старой версии. */
function VersionModal({ plan, onClose }: { plan: Plan; onClose: () => void }) {
  const qc = useQueryClient();
  const last = plan.versions[0]!;
  const [features, setFeatures] = useState<string[]>(last.features);
  const [limits, setLimits] = useState<Record<string, number | null>>(last.limits);
  const [trial, setTrial] = useState<number | null>(last.trialDays);
  const [prices, setPrices] = useState<Record<string, number | null>>(Object.fromEntries(PERIODS.map((p) => [p, last.prices.find((x) => x.period === p && x.currency === "KGS")?.amount ?? null])));
  const save = async () => {
    try {
      await http.post(`/plans/${plan.id}/versions`, { features, limits, trialDays: trial ?? 0, prices: PERIODS.filter((p) => prices[p] !== null).map((p) => ({ currency: "KGS", period: p, amount: prices[p]! })) });
      toast.success("Тариф изменён");
      void qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Изменить тариф «${plan.name}»`} description="Клиенты, которые уже платят, останутся на старых условиях, пока вы сами их не переведёте." size="xl"
      footer={<><Button onClick={() => void save()}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-5">
        <FormGrid cols={3}>
          {PERIODS.map((p) => <Field key={p} label={`Цена за ${PERIOD_LABEL[p]}, сом`}><MoneyInput value={prices[p] ?? null} onValueChange={(v) => setPrices({ ...prices, [p]: v })} /></Field>)}
          {["limit.branches", "limit.terminals", "limit.users", "quota.ai_requests"].map((l) => <Field key={l} label={featureLabel(l)} hint="Пусто — без ограничений"><NumberInput value={limits[l] ?? null} onValueChange={(v) => setLimits({ ...limits, [l]: v })} min={0} /></Field>)}
          <Field label="Пробный период, дней"><NumberInput value={trial} onValueChange={setTrial} min={0} max={90} /></Field>
        </FormGrid>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {MODULE_FEATURES.map((f) => <Checkbox key={f} label={featureLabel(f)} checked={features.includes(f)} onCheckedChange={(v) => setFeatures(v ? [...features, f] : features.filter((x) => x !== f))} />)}
        </div>
      </div>
    </Modal>
  );
}

/** Разовая плата за подключение тарифа: берётся в первом платном счёте клиента, при переходе на тариф дороже — разница. */
function SetupFeeModal({ plan, onClose }: { plan: Plan; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | null>(plan.setupFees.find((f) => f.currency === "KGS")?.amount ?? null);
  const save = async () => {
    try {
      await http.put(`/plans/${plan.id}/setup-fees`, { fees: [...plan.setupFees.filter((f) => f.currency !== "KGS"), ...(amount ? [{ currency: "KGS", amount }] : [])] });
      toast.success("Плата за подключение сохранена");
      void qc.invalidateQueries({ queryKey: ["plans"] });
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Подключение тарифа «${plan.name}»`}
      description="Разовый платёж: добавляется в первый платный счёт клиента. Если клиент переходит на тариф с подключением дороже — в счёт добавится только разница."
      footer={<><Button onClick={() => void save()}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <Field label="Сумма подключения, сом" hint="Пусто или 0 — бесплатно. Действует сразу для новых счетов, уже выставленные не меняются">
        <MoneyInput value={amount} onValueChange={setAmount} />
      </Field>
    </Modal>
  );
}

function AddonModal({ addon, plans, onClose }: { addon: Addon; plans: Plan[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(addon.name);
  const [prices, setPrices] = useState<Record<string, number | null>>(Object.fromEntries(PERIODS.map((p) => [p, addon.prices.find((x) => x.period === p && x.currency === "KGS")?.amount ?? null])));
  const [avail, setAvail] = useState<Record<string, string>>(Object.fromEntries(plans.map((p) => [p.code, addon.availability.find((a) => a.planId === p.id)?.mode ?? "purchasable"])));
  const save = async () => {
    try {
      await http.put(`/addons/${addon.code}`, {
        code: addon.code, name, description: addon.description, grantsFeatures: addon.grantsFeatures, limitDeltas: addon.limitDeltas, pricingModel: addon.pricingModel, unitLabel: addon.unitLabel,
        requiresAddons: addon.requiresAddons, trialDays: addon.trialDays, isPublic: addon.isPublic,
        prices: PERIODS.filter((p) => prices[p] !== null).map((p) => ({ currency: "KGS", period: p, amount: prices[p]! })), availability: avail,
      });
      toast.success("Сохранено");
      void qc.invalidateQueries({ queryKey: ["addons"] });
      onClose();
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={addon.name} size="lg" footer={<><Button onClick={() => void save()}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-5">
        <Field label="Название"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <FormGrid cols={3}>{PERIODS.map((p) => <Field key={p} label={`Цена за ${PERIOD_LABEL[p]}${addon.pricingModel === "per_unit" ? `, за ${addon.unitLabel ?? "шт."}` : ""}`}><MoneyInput value={prices[p] ?? null} onValueChange={(v) => setPrices({ ...prices, [p]: v })} /></Field>)}</FormGrid>
        <FormGrid cols={2}>
          {plans.map((p) => (
            <Field key={p.code} label={`На тарифе «${p.name}»`}>
              <Select value={avail[p.code] ?? "purchasable"} onValueChange={(v) => setAvail({ ...avail, [p.code]: v })} options={[{ value: "included", label: "Уже входит в тариф" }, { value: "purchasable", label: "Можно купить" }, { value: "unavailable", label: "Нельзя подключить" }]} />
            </Field>
          ))}
        </FormGrid>
      </div>
    </Modal>
  );
}

/** Каталог: тарифы (версии, цены, миграция клиентов) и дополнения (цены, доступность по тарифам). */
export default function CatalogPage() {
  const can = useCan();
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => http.get<Plan[]>("/plans") });
  const addons = useQuery({ queryKey: ["addons"], queryFn: () => http.get<Addon[]>("/addons") });
  const [version, setVersion] = useState<Plan | null>(null);
  const [setupFee, setSetupFee] = useState<Plan | null>(null);
  const [addon, setAddon] = useState<Addon | null>(null);
  const manage = can("catalog.manage");
  const migrate = (p: Plan) =>
    dialogs.confirm({
      title: `Перевести всех клиентов тарифа «${p.name}» на новые условия?`, description: "Они получат новые условия и цены. Предупредите клиентов минимум за 30 дней.", confirmText: "Перевести",
      onConfirm: async () => {
        try {
          const r = await http.post<{ migrated: number }>(`/plans/${p.id}/migrate`);
          toast.success(`Переведено компаний: ${r.migrated}`);
          void qc.invalidateQueries({ queryKey: ["plans"] });
        } catch (e) {
          toast.error(errorText(e));
        }
      },
    });
  if (plans.isPending) return <Skeleton className="h-96" />;
  return (
    <>
      <PageHeader title="Тарифы и дополнения" description="Что входит в тарифы и сколько стоит. Если изменить тариф, клиенты, которые уже платят, останутся на старых условиях." />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {plans.data?.map((p) => {
          const v = p.versions[0]!;
          const old = p.versions.slice(1).reduce((s, x) => s + x.subscriptions, 0);
          return (
            <Card key={p.id}>
              <CardHeader title={p.name} description={`Клиентов: ${v.subscriptions}${old ? ` · ещё ${old} на старых условиях` : ""}${v.version > 1 ? ` · изменён ${v.version - 1} раз` : ""}`} />
              <div className="flex flex-col gap-2 px-5 pb-5 text-sm">
                {v.prices.filter((x) => x.currency === "KGS").map((x) => <div key={x.period} className="flex justify-between"><span className="text-muted">за {PERIOD_LABEL[x.period]}</span><span className="tabular-nums">{x.amount ? money(x.amount) : "по договору"}</span></div>)}
                {(() => { const f = p.setupFees.find((x) => x.currency === "KGS")?.amount ?? 0; return <div className="flex justify-between border-t border-border pt-2"><span className="text-muted">подключение (разово)</span><span className="tabular-nums">{f ? money(f) : "бесплатно"}</span></div>; })()}
                <p className="text-xs text-muted">Функций: {v.features.length} · филиалов: {v.limits["limit.branches"] ?? "без ограничений"} · касс: {v.limits["limit.terminals"] ?? "без ограничений"}</p>
                {manage && <div className="flex flex-wrap gap-2 pt-1"><Button size="sm" onClick={() => setVersion(p)}>Изменить</Button><Button size="sm" variant="secondary" onClick={() => setSetupFee(p)}>Подключение</Button>{old > 0 && <Button size="sm" variant="secondary" onClick={() => void migrate(p)}>Перевести всех на новые условия</Button>}</div>}
              </div>
            </Card>
          );
        })}
      </div>
      <Card>
        <CardHeader title="Дополнения" description="Что можно подключить к тарифу за отдельную плату" />
        <div className="px-2 pb-3">
          <DataTable rows={addons.data} loading={addons.isPending} rowKey={(a) => a.code} onRowClick={manage ? (a) => setAddon(a) : undefined} empty={<EmptyState title="Нет" />}
            columns={[
              { key: "n", header: "Дополнение", cell: (a) => a.name, mobile: "title" },
              { key: "m", header: "Цена", cell: (a) => (a.pricingModel === "per_unit" ? `за каждую ${a.unitLabel ?? "шт."}` : "одна на компанию"), mobile: "subtitle" },
              { key: "p", header: "В месяц", align: "end", cell: (a) => { const x = a.prices.find((y) => y.period === "month" && y.currency === "KGS"); return x ? money(x.amount) : "—"; } },
              { key: "pub", header: "", cell: (a) => (a.isPublic ? null : <Badge>клиенты не видят</Badge>) },
            ]} />
        </div>
      </Card>
      {version && <VersionModal plan={version} onClose={() => setVersion(null)} />}
      {setupFee && <SetupFeeModal plan={setupFee} onClose={() => setSetupFee(null)} />}
      {addon && plans.data && <AddonModal addon={addon} plans={plans.data} onClose={() => setAddon(null)} />}
    </>
  );
}
